// Zusammenfuehren von Firmen-Schreibvarianten in Maengeln (InspectionItem.responsiblePerson).
// Das Feld ist Freitext: "Müller Bau GmbH", "Mueller Bau" und "Müller Bau Gmbh " sind
// dieselbe Firma, erscheinen in der Maengeluebersicht aber als drei. Dieses Modul findet
// die Varianten und wendet eine vom Nutzer bestaetigte Zuordnung an.
//
// UNUMKEHRBAR: Nach dem Anwenden ist die Ursprungs-Schreibweise weg. Ein Falsch-Positiv
// verschmilzt die Maengel zweier echter Firmen unter einem Namen. Deshalb ist jede
// Heuristik hier im Zweifel eng: ein uebersehener Vorschlag kostet einen manuellen Klick
// in der Oberflaeche, ein falscher Vorschlag kostet Daten.
//
// REICHWEITE: Der Merge trifft ausschliesslich die uebergebenen Items — also den
// aktuellen Arbeitsstand der Begehung. Bereits archivierte Berichte in report_history
// behalten bewusst ihre Altschreibweise: das sind versendete Dokumente, deren Inhalt
// exakt so beim Kunden liegt. Ein nachtraeglich umgeschriebener Firmenname wuerde das
// Archiv vom tatsaechlich verschickten PDF abweichen lassen.
//
// KEINE React-, Supabase- oder DOM-Abhaengigkeit — pure Funktionen, ohne jsdom testbar
// (Aufbau wie crmMatching.ts / customerDuplicates.ts).

import { InspectionItem } from '../types';
import { customerSimilarity, levenshtein, normalizeCompanyName } from './crmMatching';

// ─── Erhebung ────────────────────────────────────────────────────────────────

/** Ein in den Maengeln real vorkommender Firmen-String samt Haeufigkeit. */
export interface CompanyUsage {
  /** Der ROHE Wert wie gespeichert — inklusive Leerzeichen-Muell, nie getrimmt. */
  name: string;
  total: number;
  open: number;
  /** Erledigt = closed + good, analog zur Zaehlweise in AnalysisView. */
  done: number;
}

/**
 * Zaehlt die Firmen-Strings ueber alle Maengel — pro ROHEM Wert, damit die
 * Zuordnung spaeter exakt auf denselben String zurueckschreiben kann.
 *
 * Leere / nur aus Whitespace bestehende Werte zaehlen NICHT: das ist die
 * Sammelzeile "Nicht zugewiesen" und keine Firma, die man umbenennen duerfte.
 *
 * Eintraege mit status 'general' werden MITGEZAEHLT. Sie sind fachlich keine
 * Maengel (allgemeine Bemerkungen, siehe status-Union in types.ts), aber wenn
 * dort ein Firmenname steht, muss die Umbenennung ihn genauso treffen — sonst
 * bliebe nach dem Zusammenfuehren eine Altschreibweise im Bericht stehen.
 * In open/done landen sie nicht, weil sie keinen dieser Status tragen.
 */
export const collectCompanyUsage = (items: InspectionItem[]): CompanyUsage[] => {
  const byName = new Map<string, CompanyUsage>();

  for (const item of items || []) {
    if (!item) continue;
    const name = typeof item.responsiblePerson === 'string' ? item.responsiblePerson : '';
    if (!name.trim()) continue; // "Nicht zugewiesen" — nie Teil eines Merges

    let usage = byName.get(name);
    if (!usage) {
      usage = { name, total: 0, open: 0, done: 0 };
      byName.set(name, usage);
    }
    usage.total += 1;
    if (item.status === 'open') usage.open += 1;
    if (item.status === 'closed' || item.status === 'good') usage.done += 1;
  }

  return Array.from(byName.values()).sort(compareUsage);
};

/** Haeufigste zuerst, bei Gleichstand alphabetisch — reihenfolgeunabhaengig. */
const compareUsage = (a: CompanyUsage, b: CompanyUsage): number =>
  b.total - a.total || compareName(a.name, b.name);

/**
 * Reiner Codepoint-Vergleich statt localeCompare: localeCompare haengt an der
 * ICU-Konfiguration der Laufzeit, das Ergebnis muss aber ueberall gleich sein.
 */
const compareName = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

// ─── Rechtsformen ────────────────────────────────────────────────────────────

/**
 * Zerlegt einen ROHEN Namen in Vergleichs-Tokens: lowercase, Umlaute nach
 * ae/oe/ue/ss, Interpunktion zu Trennern. Bewusst OHNE die Rechtsform-Kappung
 * aus normalizeCompanyName — genau die Rechtsform wollen wir hier ja sehen.
 */
const foldTokens = (name: string): string[] =>
  (name || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

/**
 * Gefaltete Vergleichsform eines ROHEN Namens: "Müller Bau GmbH" und
 * "MUELLER  BAU gmbh" ergeben beide "mueller bau gmbh".
 *
 * Fuer Suche und Sortierung in der Oberflaeche gedacht: eine Suche nach
 * "müller" muss "Mueller Bau" finden, sonst bleibt ausgerechnet der haeufigste
 * Variantengrund unauffindbar. Bewusst dieselbe Faltung wie im Vergleich, damit
 * Suche und Gruppierung nicht auseinanderlaufen.
 */
export const foldCompanyName = (name: string): string => foldTokens(name).join(' ');

interface LegalFormDef {
  /** Anzeigeform fuer Warnungen in der Oberflaeche. */
  label: string;
  /** Token-Folge in gefalteter Schreibweise. */
  tokens: string[];
}

/**
 * Deutsche Rechtsformen, laengste zuerst — sonst greift bei "GmbH & Co. KG"
 * schon das blosse "gmbh" bzw. "kg". Verglichen wird auf TOKEN-Ebene, damit
 * "gmbh" nicht als "mbh" durchgeht und ein Namensbestandteil wie "agrar" nicht
 * als "ag" gelesen wird.
 */
const LEGAL_FORM_DEFS: LegalFormDef[] = [
  { label: 'GmbH & Co. KG', tokens: ['gmbh', 'und', 'co', 'kg'] },
  { label: 'GmbH & Co. KG', tokens: ['gmbh', 'co', 'kg'] },
  { label: 'UG (haftungsbeschränkt)', tokens: ['ug', 'haftungsbeschraenkt'] },
  { label: 'GmbH', tokens: ['gmbh'] },
  { label: 'mbH', tokens: ['mbh'] },
  { label: 'OHG', tokens: ['ohg'] },
  { label: 'GbR', tokens: ['gbr'] },
  { label: 'e.K.', tokens: ['e', 'k'] },
  { label: 'e.K.', tokens: ['ek'] },
  { label: 'AG', tokens: ['ag'] },
  { label: 'UG', tokens: ['ug'] },
  { label: 'KG', tokens: ['kg'] },
];

/** Kommt die Token-Folge `needle` zusammenhaengend in `tokens` vor? */
const containsTokens = (tokens: string[], needle: string[]): boolean => {
  for (let i = 0; i + needle.length <= tokens.length; i++) {
    let hit = true;
    for (let k = 0; k < needle.length; k++) {
      if (tokens[i + k] !== needle[k]) {
        hit = false;
        break;
      }
    }
    if (hit) return true;
  }
  return false;
};

/**
 * Die im ROHEN Namen tatsaechlich vorhandene Rechtsform, sonst ''.
 *
 * Wichtig fuer die Trennschaerfe: normalizeCompanyName KAPPT das Rechtsform-
 * Suffix, "Meier GmbH" und "Meier AG" normalisieren beide auf "meier". Ohne
 * diese Pruefung wuerden zwei verschiedene juristische Personen als unstrittig
 * gleich gelten ("Weber GmbH" Rohbau neben "Weber KG" Geruestbau ist auf einer
 * Baustelle voellig normal).
 */
export const detectLegalForm = (name: string): string => {
  const tokens = foldTokens(name);
  if (tokens.length === 0) return '';
  for (const def of LEGAL_FORM_DEFS) {
    if (containsTokens(tokens, def.tokens)) return def.label;
  }
  return '';
};

/**
 * Klartext-Warnungen zu einer Gruppe. Varianten OHNE Rechtsform kollidieren mit
 * nichts — "Müller" und "Müller GmbH" sind dieselbe Firma, einmal verkuerzt
 * geschrieben. Erst ZWEI VERSCHIEDENE Rechtsformen sind ein echter Verdacht.
 */
const buildWarnings = (variants: CompanyUsage[]): string[] => {
  const forms: string[] = [];
  for (const v of variants) {
    const form = detectLegalForm(v.name);
    if (form && !forms.includes(form)) forms.push(form);
  }
  if (forms.length < 2) return [];
  return [
    `Unterschiedliche Rechtsformen: ${forms.join(', ')} — das können zwei verschiedene Firmen sein.`,
  ];
};

// ─── Vorschlaege ─────────────────────────────────────────────────────────────

/** Eine Gruppe von Schreibweisen, die vermutlich dieselbe Firma sind. */
export interface CompanyMergeGroup {
  /** Deterministisch: normalisierter Name der vorgeschlagenen Ziel-Schreibweise. */
  id: string;
  /** Absteigend nach total, dann alphabetisch. */
  variants: CompanyUsage[];
  suggestedTarget: string;
  /**
   * 'sicher' = alle Varianten normalisieren auf denselben Wert UND es liegt
   * kein Rechtsform-Konflikt vor.
   */
  confidence: 'sicher' | 'wahrscheinlich';
  /**
   * Deutsche Klartext-Hinweise fuer die Oberflaeche, leer wenn nichts auffaellt.
   * Additiv ergaenzt — bestehende Felder bleiben unveraendert.
   */
  warnings: string[];
}

/**
 * Ab hier gelten zwei Schreibweisen als dieselbe Firma.
 *
 * Bewusst strenger als die 0.85 aus crmMatching: dort schlaegt ein Fehltreffer
 * nur eine Kunden-Zusammenlegung vor, die man im Wizard sieht — hier wuerde er
 * die Maengel zweier verschiedener Firmen unter einem Namen verschmelzen, und
 * die Ursprungs-Schreibweise ist danach weg (Datenverlust). Ein uebersehener
 * Vorschlag kostet dagegen nur einen manuellen Klick.
 *
 * 0.88 laesst einen Tippfehler in Namen ab ~9 Zeichen noch durch
 * ("mueler bau" / "mueller bau" = 0.91) und haelt echte Namensvettern
 * draussen ("elektro schmidt" / "elektro schneider" = 0.76).
 *
 * Die Schwelle allein reicht aber NICHT — siehe DIGIT- und WORT-Sperre unten.
 * Sie einfach anzuheben wuerde die echten Tippfehler mit erschlagen.
 *
 * Bekannte Luecke aus derselben Strenge: ein ausgeschriebenes "und" gegen "&"
 * ("Meier & Söhne" / "Meier und Söhne" = 0.75) findet nicht zusammen — ein
 * ganzes Zusatz-Token laesst sich nicht sicher von einem echten dritten
 * Namensbestandteil unterscheiden. Diese Faelle bleiben Handarbeit.
 */
export const DEFAULT_MERGE_THRESHOLD = 0.88;

/**
 * Wortsperre: Mindest-Aehnlichkeit eines ABWEICHENDEN Tokens.
 *
 * Das Problem der Gesamtstring-Metrik: die Levenshtein-Ratio verduennt einen
 * Unterschied, je laenger der gemeinsame Rest ist. "maler maier" / "maler meier"
 * unterscheidet sich in genau einem Zeichen von elf -> 1 - 1/11 = 0.9091 und lag
 * damit ueber 0.88, obwohl Maier und Meier zwei verschiedene Nachnamen sind.
 * Derselbe eine Zeichenunterschied ergibt auf Token-Ebene ("maier" / "meier",
 * 5 Zeichen) nur 1 - 1/5 = 0.8000.
 *
 * Gegenprobe A — echte Tippfehler, die weiter gefunden werden MUESSEN:
 *   "mueler"     / "mueller"     1 - 1/7  = 0.8571
 *   "schmit"     / "schmidt"     1 - 1/7  = 0.8571
 *   "trokenbau"  / "trockenbau"  1 - 1/10 = 0.9000
 *   "wilhelmson" / "wilhelmsen"  1 - 1/10 = 0.9000
 *
 * Gegenprobe B — Namensvettern, die draussen bleiben MUESSEN:
 *   "maier"      / "meier"       1 - 1/5  = 0.8000
 *   "meier"      / "meyer"       1 - 1/5  = 0.8000
 *   "maier"      / "meyer"       1 - 2/5  = 0.6000
 *   "wilhelmsen" / "wihelmson"   1 - 2/10 = 0.8000
 *
 * 0.85 liegt zwischen 0.8000 (hoechster Wert aus B) und 0.8571 (niedrigster aus
 * A) und trennt beide Mengen sauber. In Klartext heisst die Grenze: ein
 * einzelner Zeichenfehler wird erst ab 7 Zeichen Tokenlaenge toleriert
 * (1 - 1/7 = 0.8571 >= 0.85; bei 6 Zeichen waeren es 1 - 1/6 = 0.8333 und damit
 * zu wenig). Genau das ist die gewollte Aussage — in einem kurzen Nachnamen ist
 * ein Buchstabe bedeutungstragend, in einem langen Wort ist er ein Vertipper.
 *
 * Bewusst in Kauf genommene Grenze: "schmidt" / "schmitt" liegt ebenfalls bei
 * 0.8571 und rutscht durch. Das sind zwei echte Nachnamen, die zeichenweise
 * exakt so weit auseinanderliegen wie das Tippfehler-Paar "schmit"/"schmidt" —
 * rein metrisch nicht trennbar. Solche Gruppen entstehen nur als
 * 'wahrscheinlich' und muessen im Dialog bestaetigt werden.
 */
export const TOKEN_MIN_RATIO = 0.85;

/**
 * Gruppiert Schreibvarianten in zwei getrennten Zweigen:
 *
 * 1. NORMALISIERT IDENTISCH — transitiv. Gleiche Normalisierung ist eine echte
 *    Aequivalenzrelation (nur Gross-/Kleinschreibung, Umlaut-Variante,
 *    Interpunktion, Rechtsform oder Whitespace unterscheiden sich), da kann
 *    keine Bruecke ueber eine fremde Firma entstehen.
 *
 * 2. UNSCHARF — Complete-Linkage. Eine Variante tritt nur bei, wenn sie zu
 *    ALLEN bisherigen Mitgliedern ueber der Schwelle liegt. Das frueher
 *    verwendete Single-Linkage baute Bruecken: "Maler Maier" ~ "Maler Meier" ~
 *    "Maler Meyer" landeten zu dritt in einer Gruppe, obwohl "Maler Maier" und
 *    "Maler Meyer" direkt nur 0.8182 ergeben.
 *
 * Complete-Linkage ist von Natur aus reihenfolgeabhaengig. Deterministisch wird
 * es durch zwei Festlegungen: die Eingabe wird IMMER nach compareUsage
 * vorsortiert (haeufigste zuerst, dann Codepoint), und jede Variante tritt der
 * ersten Gruppe bei, zu der sie passt. Weil alle Vergleiche reine Funktionen des
 * normalisierten Namens sind, liefert eine umgedrehte Eingabe dasselbe Ergebnis.
 *
 * Zurueck kommen nur Gruppen mit mindestens 2 Varianten — bei einer einzelnen
 * Schreibweise gibt es nichts zusammenzufuehren.
 */
export const suggestCompanyMerges = (
  usages: CompanyUsage[],
  threshold: number = DEFAULT_MERGE_THRESHOLD,
): CompanyMergeGroup[] => {
  const list = (usages || []).filter((u) => u && u.name.trim()).slice().sort(compareUsage);
  const norms = list.map((u) => normalizeCompanyName(u.name));

  const clusters: CompanyUsage[][] = [];
  const assigned = new Set<number>();

  for (let i = 0; i < list.length; i++) {
    if (assigned.has(i)) continue;
    const members = [i];
    assigned.add(i);

    // Ein Durchlauf genuegt: Complete-Linkage kann durch ein neues Mitglied nur
    // strenger werden, nie lockerer. Der Aequivalenz-Zweig ist transitiv, alle
    // gleich normalisierten Varianten haengen sich am Seed selbst ein.
    for (let j = i + 1; j < list.length; j++) {
      if (assigned.has(j)) continue;
      const equalToMember = !!norms[j] && members.some((m) => norms[m] === norms[j]);
      const similarToAll =
        !equalToMember && members.every((m) => isFuzzySameCompany(list[m].name, list[j].name, threshold));
      if (equalToMember || similarToAll) {
        members.push(j);
        assigned.add(j);
      }
    }

    if (members.length > 1) clusters.push(members.map((m) => list[m]));
  }

  const groups = clusters.map((cluster) => {
    const variants = cluster.slice().sort(compareUsage);
    const suggestedTarget = pickTarget(variants);
    const normalized = new Set(variants.map((v) => normalizeCompanyName(v.name)));
    const warnings = buildWarnings(variants);
    return {
      id: normalizeCompanyName(suggestedTarget) || suggestedTarget.trim().toLowerCase(),
      variants,
      suggestedTarget,
      // 'sicher' nur, wenn alle Varianten auf denselben Wert normalisieren UND
      // keine zwei verschiedenen Rechtsformen im Spiel sind: "Meier GmbH" und
      // "Meier AG" normalisieren beide auf "meier", sind aber zwei Firmen.
      confidence: (normalized.size === 1 && warnings.length === 0
        ? 'sicher'
        : 'wahrscheinlich') as CompanyMergeGroup['confidence'],
      warnings,
    };
  });

  return groups.sort(
    (a, b) => sumTotal(b.variants) - sumTotal(a.variants) || compareName(a.id, b.id),
  );
};

const sumTotal = (variants: CompanyUsage[]): number =>
  variants.reduce((sum, v) => sum + v.total, 0);

// ─── Trennschaerfe ───────────────────────────────────────────────────────────

/** Normalisierte Levenshtein-Ratio 0..1 (wie in crmMatching, dort nicht exportiert). */
const levRatio = (a: string, b: string): number => {
  const maxLen = Math.max(a.length, b.length);
  return maxLen === 0 ? 1 : 1 - levenshtein(a, b) / maxLen;
};

/**
 * Ziffernsperre: die Ziffernfolgen beider Namen muessen identisch sein.
 *
 * Zahlen in Firmennamen sind Jahreszahlen, Standort- oder Objektnummern oder
 * echte Namensbestandteile — nie Tippfehler-Rauschen. Ohne diese Sperre haetten
 * "Bau 2000"/"Bau 20000" (0.8889) und "Hochbau 2000"/"Hochbau 2001" (0.9167)
 * als dieselbe Firma gegolten. "Bau 2000"/"BAU 2000 GmbH" traegt dieselbe
 * Ziffernfolge und bleibt erhalten.
 *
 * Bei kurzen, allein stehenden Zahlen faengt die Wortsperre dieselben Faelle
 * schon ab ("2000"/"2001" = 0.7500). Die Ziffernsperre ist die allgemeinere
 * Aussage und greift auch dort, wo die Zahl am Wort klebt und der Unterschied
 * damit in einem langen Token verschwindet ("holzbau2000"/"holzbau2001" =
 * 0.9091, kaeme durch die Wortsperre durch).
 */
const digitRuns = (normalized: string): string => (normalized.match(/\d+/g) || []).join('|');

/** Tokens von `ta`, die in `tb` kein gleiches Gegenstueck haben (Multimengen-Differenz). */
const unmatchedTokens = (ta: string[], tb: string[]): string[] => {
  const pool = tb.slice();
  const rest: string[] = [];
  for (const t of ta) {
    const idx = pool.indexOf(t);
    if (idx >= 0) pool.splice(idx, 1);
    else rest.push(t);
  }
  return rest;
};

/**
 * Wortsperre: Liegt der Unterschied in Tokens, die fuer sich genommen zu
 * unaehnlich sind? Dann ist es NICHT dieselbe Firma (Begruendung der Grenze
 * siehe TOKEN_MIN_RATIO).
 *
 * Angewendet wird die Sperre nur, wenn beide Seiten GLEICH VIELE abweichende
 * Tokens haben — dann lassen sie sich der Reihe nach paaren. Bei ungleicher
 * Anzahl fehlt oder steht ein ganzes Wort zuviel; dieser Fall braucht keine
 * Extra-Sperre, weil die Laengendifferenz die Gesamt-Ratio ohnehin drueckt
 * ("mueller bau"/"mueller bau nord" = 0.6875). Und er darf keine bekommen,
 * sonst faellt der zusammengeschriebene Tippfehler heraus
 * ("muellerbau"/"mueller bau" = 0.9091).
 *
 * Gleiche Tokens auf beiden Seiten fallen vorher heraus, damit eine reine
 * Umstellung ("Bau Müller"/"Müller Bau") nicht faelschlich gesperrt wird.
 */
const tokensTooDifferent = (na: string, nb: string): boolean => {
  const restA = unmatchedTokens(na.split(' ').filter(Boolean), nb.split(' ').filter(Boolean));
  const restB = unmatchedTokens(nb.split(' ').filter(Boolean), na.split(' ').filter(Boolean));
  if (restA.length === 0 || restA.length !== restB.length) return false;
  for (let i = 0; i < restA.length; i++) {
    if (levRatio(restA[i], restB[i]) < TOKEN_MIN_RATIO) return true;
  }
  return false;
};

/**
 * Unscharfer Zweig: Ziffernsperre und Wortsperre muessen passieren, erst dann
 * entscheidet die Gesamt-Aehnlichkeit. Die Sperren wirken auch dann, wenn die
 * Gesamt-Metrik ueber der Schwelle liegt — sie sind Vetos, keine Zuschlaege.
 */
const isFuzzySameCompany = (a: string, b: string, threshold: number): boolean => {
  const na = normalizeCompanyName(a);
  const nb = normalizeCompanyName(b);
  if (!na || !nb) return false;
  if (digitRuns(na) !== digitRuns(nb)) return false;
  if (tokensTooDifferent(na, nb)) return false;
  return customerSimilarity({ name: a }, { name: b }) >= threshold;
};

/**
 * Gleiche Firma? Identische Normalisierung zaehlt immer (auch wenn davon nur
 * ein sehr kurzer Rest uebrig bleibt), sonst entscheidet der unscharfe Zweig.
 */
export const isSameCompany = (a: string, b: string, threshold: number = DEFAULT_MERGE_THRESHOLD): boolean => {
  const na = normalizeCompanyName(a);
  const nb = normalizeCompanyName(b);
  if (na && nb && na === nb) return true;
  return isFuzzySameCompany(a, b, threshold);
};

// ─── Ziel-Schreibweise ───────────────────────────────────────────────────────

/** Echte Umlaute/ß statt der ae/oe/ue/ss-Ersatzschreibung? */
const hasRealUmlaut = (name: string): boolean => /[äöüßÄÖÜ]/.test(name);

/**
 * Ziel-Schreibweise: VOLLSTAENDIGKEIT SCHLAEGT HAEUFIGKEIT.
 *
 * Frueher gewann schlicht die haeufigste Variante. Damit landete "Mueller Bau"
 * (9 Maengel) vor "Müller Bau GmbH" (2 Maengel) — und genau dieser verstuemmelte
 * Name stand danach im gedruckten Bericht beim Kunden. Haeufigkeit misst aber
 * nur die Tippgewohnheit der erfassenden Person, nicht die Richtigkeit der
 * Firmierung. Deshalb die bewusste Umkehr:
 *
 *  1. traegt eine Rechtsform  -> die vollstaendige Firmierung ("… GmbH"),
 *  2. echte Umlaute/ß         -> "Müller" statt "Mueller",
 *  3. Haeufigkeit (total),
 *  4. Laenge OHNE aeussere Leerzeichen,
 *  5. bei gleicher getrimmter Laenge: die bereits saubere Variante,
 *  6. alphabetisch (Codepoint, nicht localeCompare — Laufzeit-unabhaengig).
 *
 * Kriterium 1 und 2 sind unabhaengig voneinander: "Mueller Bau GmbH" schlaegt
 * "Müller Bau", weil die Rechtsform der groessere Informationsgewinn ist — der
 * Umlaut laesst sich nachtippen, die fehlende Rechtsform nicht erraten.
 *
 * Kriterium 4 zaehlt bewusst die GETRIMMTE Laenge: "Müller Bau GmbH " ist roh
 * ein Zeichen laenger als "Müller Bau GmbH", traegt aber keine Information
 * mehr, nur ein verirrtes Leerzeichen. Ohne das Trimmen gewann der Muell-String
 * den Laengenvergleich und stand danach so im gedruckten Bericht. Kriterium 5
 * entscheidet denselben Fall endgueltig zugunsten der sauberen Schreibweise.
 */
const pickTarget = (variants: CompanyUsage[]): string => {
  let best = variants[0];
  for (const v of variants.slice(1)) {
    if (isBetterTarget(v, best)) best = v;
  }
  return best.name;
};

const isBetterTarget = (v: CompanyUsage, best: CompanyUsage): boolean => {
  const vForm = detectLegalForm(v.name) !== '';
  const bForm = detectLegalForm(best.name) !== '';
  if (vForm !== bForm) return vForm;

  const vUmlaut = hasRealUmlaut(v.name);
  const bUmlaut = hasRealUmlaut(best.name);
  if (vUmlaut !== bUmlaut) return vUmlaut;

  if (v.total !== best.total) return v.total > best.total;

  const vTrimmed = v.name.trim();
  const bTrimmed = best.name.trim();
  if (vTrimmed.length !== bTrimmed.length) return vTrimmed.length > bTrimmed.length;

  // Gleicher Inhalt, nur einer traegt aussen Leerzeichen: der saubere gewinnt.
  const vClean = vTrimmed === v.name;
  const bClean = bTrimmed === best.name;
  if (vClean !== bClean) return vClean;

  return compareName(v.name, best.name) < 0;
};

// ─── Anwenden ────────────────────────────────────────────────────────────────

/**
 * Wirksame Zuordnungen aus einem rohen Mapping (ROHER Quell-String -> Ziel).
 *
 * Verworfen werden:
 *  - leere/Whitespace-Quellen: das waeren die "Nicht zugewiesen"-Maengel, die
 *    nie versehentlich einer Firma zugeschlagen werden duerfen,
 *  - leere/Whitespace-Ziele: sonst loeschte ein Fehlklick eine Firma aus allen
 *    Maengeln,
 *  - Ziel == Quelle: aendert nichts.
 *
 * KETTEN werden bis zum Endziel aufgeloest: aus {A:B, B:C} wird A->C und B->C.
 * Ohne diese Aufloesung zerfiel die Zusammenfuehrung — A landete auf B, B auf C,
 * und am Ende standen wieder zwei Schreibweisen in den Maengeln.
 *
 * ZYKLEN bleiben unveraendert: {A:B, B:A} wuerde die Namen nur tauschen, was
 * niemand meinen kann. Jeder Eintrag, dessen Kette in einen Zyklus laeuft (auch
 * {A:B, B:C, C:B}), wird komplett verworfen — es gibt kein definiertes Endziel.
 * Lieber gar nichts aendern als Namen vertauschen.
 */
const effectiveMapping = (mapping: Record<string, string>): Map<string, string> => {
  const raw = new Map<string, string>();
  for (const [source, target] of Object.entries(mapping || {})) {
    if (!source.trim()) continue;
    if (typeof target !== 'string' || !target.trim()) continue;
    if (target === source) continue;
    raw.set(source, target);
  }

  const out = new Map<string, string>();
  for (const source of raw.keys()) {
    const seen = new Set<string>([source]);
    let target = raw.get(source) as string;
    let cyclic = false;

    while (raw.has(target)) {
      if (seen.has(target)) {
        cyclic = true;
        break;
      }
      seen.add(target);
      target = raw.get(target) as string;
    }

    if (cyclic) continue;
    if (target === source) continue;
    out.set(source, target);
  }

  return out;
};

/**
 * Schreibt die Zuordnung in die Maengel. Exakter Stringvergleich auf
 * responsiblePerson — die Quellen stammen aus collectCompanyUsage und sind
 * damit genau die gespeicherten Werte.
 *
 * Gibt ein NEUES Array zurueck; unveraenderte Items werden per Referenz
 * durchgereicht, damit React-Vergleiche billig bleiben.
 */
export const applyCompanyMerge = (
  items: InspectionItem[],
  mapping: Record<string, string>,
): { items: InspectionItem[]; changed: number } => {
  const effective = effectiveMapping(mapping);
  const source = items || [];
  if (effective.size === 0) return { items: source.slice(), changed: 0 };

  let changed = 0;
  const next = source.map((item) => {
    if (!item) return item;
    const target = effective.get(item.responsiblePerson);
    if (target === undefined) return item; // unveraendert -> gleiche Referenz
    changed += 1;
    return { ...item, responsiblePerson: target };
  });

  return { items: next, changed };
};

/** Was diese Zuordnung tatsaechlich bewirken wuerde. */
export interface MergeSummary {
  /** Maengel, deren Firmenname sich aendert (identisch zu countAffected). */
  affected: number;
  /** Schreibweisen, die wirksam umbenannt werden. */
  sources: number;
  /** Firmen, auf die zusammengefuehrt wird. */
  targets: number;
}

/**
 * Vorschau-Zahlen aus GENAU derselben Aufloesung, die auch applyCompanyMerge
 * verwendet — inklusive Ketten- und Zyklus-Aufloesung.
 *
 * Die Oberflaeche darf diese Zahlen NICHT selbst aus dem rohen Mapping
 * ableiten: {A:B, B:A} sieht dort nach zwei Umbenennungen aus, wird beim
 * Anwenden aber komplett verworfen. Der Nutzer haette einen aktiven
 * Anwenden-Knopf gesehen, hinter dem nichts passiert. Eine Kette {A:B, B:C}
 * ist der umgekehrte Fall: roh zwei Ziele, wirksam nur eines (C).
 */
export const summarizeMerge = (
  items: InspectionItem[],
  mapping: Record<string, string>,
): MergeSummary => {
  const effective = effectiveMapping(mapping);
  if (effective.size === 0) return { affected: 0, sources: 0, targets: 0 };

  let affected = 0;
  for (const item of items || []) {
    if (item && effective.has(item.responsiblePerson)) affected += 1;
  }

  return {
    affected,
    sources: effective.size,
    targets: new Set(effective.values()).size,
  };
};

/**
 * Wie viele Maengel wuerde diese Zuordnung aendern? Fuer die Rueckfrage vor
 * dem Zusammenfuehren — zaehlt ueber DIESELBE effectiveMapping wie
 * applyCompanyMerge, inklusive Ketten- und Zyklus-Aufloesung. Beide Zahlen
 * muessen immer uebereinstimmen, sonst zeigt die Rueckfrage etwas anderes an,
 * als anschliessend passiert.
 */
export const countAffected = (
  items: InspectionItem[],
  mapping: Record<string, string>,
): number => summarizeMerge(items, mapping).affected;
